import { point, polygon, lineString, featureCollection, feature } from '@turf/helpers';
import { arcgisToGeoJSON } from '@esri/arcgis-to-geojson-utils';
import proj4 from 'proj4';
export const createPointFeature = ({ coordinates, id, properties, lat, long, ...rest }) => {
	if (coordinates && (lat || long)) {
		throw new Error(
			'Invalid parameters - Provide either a coordinates parameter OR lat and long parameters.'
		);
	}

	if (!coordinates && (lat === undefined || long === undefined)) {
		throw new Error('The coordinates parameter OR lat and long parameters are required.');
	}
	const coords = coordinates ? coordinates : [long, lat];
	const geoJSONPoint = point(coords, properties, { id });
	return {
		...geoJSONPoint,
		...rest
	};
};

export const createPolygonFeature = ({ coordinates, id, properties, ...rest }) => {
	const geoJSONPolygon = polygon(coordinates, properties, { id });
	return {
		...geoJSONPolygon,
		...rest
	};
};

export const createLineStringFeature = ({ coordinates, id, properties, ...rest }) => {
	const geoJSONLineString = lineString(coordinates, properties, { id });
	return {
		...geoJSONLineString,
		...rest
	};
};

export const createFeatureCollection = ({ features, id, ...rest }) => {
	const geoJSONFeatureCollection = featureCollection(features, { id });
	return {
		...geoJSONFeatureCollection,
		...rest
	};
};

export const createGeoJSONFeatureLayer = ({ type, id, feature, paint, layout }) => {
	return {
		type,
		id,
		source: {
			type: 'geojson',
			data: feature
		},
		paint: {
			...paint
		},
		layout: {
			...layout
		}
	};
};

/**
 * Converts GeometryCollection features with homogenous LineString or Polygon types
 * into MultiLineString or MultiPolygon. Leaves other features untouched.
 */
export function convertGeometryCollectionToFeatureCollection(input) {
	if (input.type !== 'FeatureCollection' || !Array.isArray(input.features)) {
		throw new Error('Input must be a FeatureCollection');
	}

	const normalizedFeatures = input.features.map(f => {
		if (
			f?.type === 'Feature' &&
			f.geometry?.type === 'GeometryCollection' &&
			Array.isArray(f.geometry.geometries) &&
			f.geometry.geometries.length > 0
		) {
			const type = f.geometry.geometries[0].type;
			const allSameType = f.geometry.geometries.every(g => g.type === type);

			if (!allSameType) return f; // skip mixed geometry types

			let multiType;
			if (type === 'LineString') {
				multiType = 'MultiLineString';
			} else if (type === 'Polygon') {
				multiType = 'MultiPolygon';
			} else if (type === 'Point') {
				multiType = 'MultiPoint';
			} else {
				return f; // skip unsupported geometry types
			}

			const coordinates = f.geometry.geometries.map(g => g.coordinates);
			return feature({ type: multiType, coordinates }, f.properties || {});
		}

		// Return untouched feature
		return f;
	});

	return featureCollection(normalizedFeatures);
}

/**
 * Normalize a FeatureCollection of Point or Polygon features into a single MultiPoint or MultiPolygon.
 * If it's already normalized, returns the original FeatureCollection.
 */
export function normalizeToMultiGeometry(fc) {
	if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
		throw new Error('Input must be a FeatureCollection');
	}

	const features = fc.features;
	if (features.length === 0) return fc;

	const geomType = features[0].geometry?.type;

	// Handle already normalized cases
	if (features.length === 1 && (geomType === 'MultiPolygon' || geomType === 'MultiPoint')) {
		return fc;
	}

	// Normalize Polygons
	const polygonFeatures = features.filter(f => f.geometry?.type === 'Polygon');
	if (polygonFeatures.length > 1) {
		const coordinates = polygonFeatures.map(f => f.geometry.coordinates);
		const props = polygonFeatures[0].properties || {};
		return featureCollection([
			feature(
				{
					type: 'MultiPolygon',
					coordinates
				},
				props
			)
		]);
	}

	// Normalize Points
	const pointFeatures = features.filter(f => f.geometry?.type === 'Point');
	if (pointFeatures.length > 1) {
		const coordinates = pointFeatures.map(f => f.geometry.coordinates);
		const props = pointFeatures[0].properties || {};
		return featureCollection([
			feature(
				{
					type: 'MultiPoint',
					coordinates
				},
				props
			)
		]);
	}

	// If there's only one Point or Polygon, or it's not a type we handle, return as-is
	return fc;
}
export const mergeFeatureCollectionToSingleFeature = fc => {
	if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
		throw new Error('Input must be a valid GeoJSON FeatureCollection');
	}

	const features = fc.features;
	if (features.length <= 1) return fc;

	const supported = ['Point', 'LineString', 'Polygon'];
	const typeGroups = features.map(f => f.geometry?.type);

	// Determine base type
	let base = null;
	for (let t of supported) {
		if (typeGroups.some(gt => gt === t || gt === `Multi${t}`)) {
			base = t;
			break;
		}
	}

	if (!base) {
		throw new Error('Unsupported geometry types for merging');
	}

	const mergedCoordinates = features.flatMap(f => {
		const { type, coordinates } = f.geometry;

		if (type === base) {
			return [coordinates]; // wrap
		}
		if (type === `Multi${base}`) {
			return coordinates;
		}
		throw new Error(`Geometry type mismatch: ${type}`);
	});

	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				geometry: {
					type: `Multi${base}`,
					coordinates: mergedCoordinates
				},
				properties: {}
			}
		]
	};
};

export const convertEsriToGeoJSON = esriFeatures => {
	const fromProjection = 'EPSG:3857'; // Web Mercator
	const toProjection = 'EPSG:4326'; // WGS84 (lon/lat)
	return {
		type: 'FeatureCollection',
		features: esriFeatures.map(f => {
			const geo = arcgisToGeoJSON(f);

			if (geo.geometry && geo.geometry.coordinates) {
				geo.geometry.coordinates = proj4(fromProjection, toProjection, geo.geometry.coordinates);
			}

			return geo;
		})
	};
};
